/**
 * PURPOSE:
 * Ruta pública HAYLLIS para árbitros de vóley: /modulo-visualizacion?idmatch=...
 *
 * RESPONSIBILITIES:
 * - Pedir nombre + código.
 * - Conectar Socket.IO y mostrar cancha read-only.
 * - Resincronizar si hay reconexión o pérdida de red.
 * - Usar header simple propio, no el layout general de HAYLLIS.
 * - Mantener la vista autenticada comprimida en 100dvh para uso arbitral en celular.
 *
 * ROUTE:
 * src/pages/VolleyballViewer/VolleyballViewerPage.jsx
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ViewerAccessGate from "./components/ViewerAccessGate.jsx";
import ViewerCompactMatchStrip from "./components/ViewerCompactMatchStrip.jsx";
import ViewerCourt from "./components/ViewerCourt.jsx";
import ViewerHeader from "./components/ViewerHeader.jsx";
import { useVolleyballViewerAuth } from "./hooks/useVolleyballViewerAuth.js";
import { useVolleyballViewerSocket } from "./hooks/useVolleyballViewerSocket.js";

export default function VolleyballViewerPage() {
  const [searchParams] = useSearchParams();
  const matchId = Number(searchParams.get("idmatch")) || null;
  const rootRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [swapped, setSwapped] = useState(false);

  const auth = useVolleyballViewerAuth(matchId);
  const socket = useVolleyballViewerSocket({
    matchId,
    session: auth.session,
    enabled: !!auth.session,
    onKicked: auth.logout,
    onDisabled: auth.loadStatus,
  });

  useEffect(() => {
    function handleFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!rootRef.current) return;
      if (!document.fullscreenElement) await rootRef.current.requestFullscreen?.();
      else await document.exitFullscreen?.();
    } catch {
      // No bloquea la visualización si el navegador no permite fullscreen.
    }
  }, []);

  const logout = useCallback(() => {
    auth.logout();
    socket.setPayload(null);
    socket.setTimeoutOverlay(null);
  }, [auth, socket]);

  if (!matchId) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-950 p-4 text-white">
        <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-center">
          <div className="text-xl font-black">Falta idmatch</div>
          <p className="mt-2 text-sm text-white/70">Abre la ruta con /modulo-visualizacion?idmatch=...</p>
        </div>
      </main>
    );
  }

  if (!auth.session) {
    return (
      <ViewerAccessGate
        status={auth.status}
        loading={auth.loading}
        error={auth.error}
        onSubmit={auth.login}
      />
    );
  }

  const payload = socket.payload;

  return (
    <div ref={rootRef} className="flex h-[100dvh] overflow-hidden bg-slate-100 text-slate-950">
      <div className="flex min-h-0 w-full flex-col">
        <ViewerHeader
          connectionState={socket.connectionState}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onResync={socket.requestSocketResync}
          onLogout={logout}
        />

        <main className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-1.5 px-1.5 py-1.5 sm:max-w-2xl sm:px-3 sm:py-2">
          {socket.error ? (
            <div className="shrink-0 truncate rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-800">
              {socket.error}
            </div>
          ) : null}

          {!payload ? (
            <div className="grid min-h-0 flex-1 place-items-center rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
              <div>
                <div className="text-base font-black text-slate-950">Sincronizando estado...</div>
                <button
                  type="button"
                  onClick={socket.resync}
                  className="mt-3 rounded-xl bg-slate-900 px-4 py-2 text-xs font-black uppercase tracking-[0.1em] text-white"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : (
            <>
              <ViewerCourt payload={payload} swapped={swapped} />
              <ViewerCompactMatchStrip payload={payload} swapped={swapped} timeoutOverlay={socket.timeoutOverlay} />
              <button
                type="button"
                onClick={() => setSwapped((prev) => !prev)}
                className="h-10 shrink-0 rounded-xl bg-indigo-700 px-4 text-xs font-black uppercase tracking-[0.12em] text-white shadow-sm transition active:scale-[0.99]"
              >
                Cambiar lados
              </button>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
